import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function ImmigrationNew() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ priority: "Medium" });
  const [existingClient, setExistingClient] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [clientMatches, setClientMatches] = useState([]);

  useEffect(() => { api.get("/immigration/meta").then(r=>setMeta(r.data)); }, []);
  useEffect(() => {
    if (!clientQuery.trim() || existingClient) { setClientMatches([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get(`/immigration/clients?q=${encodeURIComponent(clientQuery.trim())}`);
      setClientMatches(r.data.slice(0, 5));
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery, existingClient]);

  if (!meta) return <div className="text-slate-500">Loading…</div>;

  const set = (k, v) => setF(o => ({ ...o, [k]: v }));

  const submit = async () => {
    try {
      const body = { ...f };
      if (existingClient) body.client_id = existingClient.id;
      if (!body.app_type_id) { toast.error("Application Type required"); return; }
      if (!existingClient) {
        if (!body.full_name || !body.email || !body.phone) { toast.error("Name, Email, Phone required"); return; }
      }
      const r = await api.post("/immigration/applications", body);
      toast.success(`Created ${r.data.application_number}`);
      nav(`/immigration/applications/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">New Immigration Application</h1>
        <p className="text-sm text-slate-500 mt-1">Enter the essentials — Filing Manager will assign an officer next.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div>
          <div className="heading-font font-semibold mb-2">Client</div>
          {existingClient ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
              <span><strong>{existingClient.full_name}</strong> · {existingClient.email} · {existingClient.phone}</span>
              <Button variant="ghost" size="sm" onClick={()=>{ setExistingClient(null); setClientQuery(""); }}>Change</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="imm-client-search" placeholder="Search existing client by name / email / phone / UCI…"
                       value={clientQuery} onChange={e=>setClientQuery(e.target.value)} className="pl-9" />
              </div>
              {clientMatches.length > 0 && (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                  {clientMatches.map(c => (
                    <button key={c.id} type="button" onClick={()=>{ setExistingClient(c); setClientMatches([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                      <div className="font-medium">{c.full_name}</div>
                      <div className="text-xs text-slate-500">{c.email} · {c.phone} {c.uci ? ` · UCI ${c.uci}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
              <div className="text-xs text-slate-500">Or enter a brand-new client below:</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Full Name *"><Input data-testid="imm-full-name" value={f.full_name||""} onChange={e=>set("full_name", e.target.value)} /></F>
                <F label="Email *"><Input data-testid="imm-email" type="email" value={f.email||""} onChange={e=>set("email", e.target.value)} /></F>
                <F label="Phone *"><Input data-testid="imm-phone" value={f.phone||""} onChange={e=>set("phone", e.target.value)} /></F>
                <F label="UCI"><Input value={f.uci||""} onChange={e=>set("uci", e.target.value)} /></F>
                <div className="sm:col-span-2"><F label="Address"><Input value={f.address||""} onChange={e=>set("address", e.target.value)} /></F></div>
              </div>
            </div>
          )}
        </div>

        <div className="heading-font font-semibold mt-3">Application</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Application Type *">
            <Select value={f.app_type_id||""} onValueChange={v=>set("app_type_id", v)}>
              <SelectTrigger data-testid="imm-type"><SelectValue placeholder="Choose type"/></SelectTrigger>
              <SelectContent>{meta.app_types.map(t=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Priority">
            <Select value={f.priority} onValueChange={v=>set("priority", v)}>
              <SelectTrigger data-testid="imm-priority"><SelectValue/></SelectTrigger>
              <SelectContent>{meta.priorities.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Current Status Expiry"><Input type="date" value={f.status_expiry_date||""} onChange={e=>set("status_expiry_date", e.target.value)} /></F>
          <F label="Professional Fee (CAD)"><Input type="number" value={f.professional_fee||""} onChange={e=>set("professional_fee", Number(e.target.value)||0)} /></F>
          <F label="Clio Matter URL"><Input placeholder="https://…" value={f.clio_matter_url||""} onChange={e=>set("clio_matter_url", e.target.value)} /></F>
          <F label="Google Drive Folder URL"><Input placeholder="https://…" value={f.drive_folder_url||""} onChange={e=>set("drive_folder_url", e.target.value)} /></F>
          <div className="sm:col-span-2"><F label="Notes"><Textarea value={f.notes||""} onChange={e=>set("notes", e.target.value)} /></F></div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={()=>nav("/immigration/applications")}>Cancel</Button>
          <Button data-testid="imm-create-btn" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Create & Send to Filing</Button>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>
);
